import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  WidthType,
  AlignmentType,
  TextRun,
  ImageRun,
  BorderStyle,
} from "docx";
import { Tela } from '../../shared/enumerable/tipo-tela-enum';

declare module 'jspdf' {
  interface jsPDF {
    autoTable(options: any): jsPDF;
    autoTable(columns: any[], rows: any[], options?: any): jsPDF;
  }
}

export interface Coluna {
  nome: string;
  campo: string;
}
@Injectable({
  providedIn: 'root',
})
export class ExportarTabelaService {
  tipoDadoParaExportar(tipo: number, dados: any[], colunas: Coluna[], nomeTabela: string) {
    if (tipo === 1) {
      this.exportarTXT(dados, colunas, nomeTabela);
    } else if (tipo === 2) {
      this.exportarExcel(dados, colunas, nomeTabela);
    } else if (tipo === 3) {
      this.exportarPDF(dados, colunas, nomeTabela);
    } else if (tipo === 4) {
      this.exportarWord(dados, colunas, nomeTabela);
    } else if (tipo === 5) {
      this.exportarCSV(dados, colunas, nomeTabela);
    }
  }

  private exportarCSV(data: any[], colunas: Coluna[], nomeTabela: string) {
    const dadosFormatados = data.map((dado) =>
      colunas.reduce<{ [key: string]: any }>((acc, coluna) => {
        acc[coluna.nome] = dado[coluna.campo];
        return acc;
      }, {})
    );

    const worksheet = XLSX.utils.json_to_sheet(dadosFormatados);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, nomeTabela);

    const csvData = XLSX.write(workbook, { bookType: 'csv', type: 'array' });
    saveAs(new Blob([csvData], { type: 'text/csv' }), `${nomeTabela}.csv`);
  }

  private exportarPDF(data: any[], colunas: Coluna[], nomeTabela: string) {
    const doc = new jsPDF();
    const colunasNomes = colunas.map((coluna) => coluna.nome);
    const linhas = data.map((dado) => colunas.map((coluna) => dado[coluna.campo]));
    const logoUrl = '../../../../assets/app-assets/images/logo/logo-b-geia.png';
    const imgWidth = 50;
    const imgHeight = 20;
    const marginTop = 10;
    const marginLeft = 10;
    const titulo = nomeTabela;
    const tituloX = doc.internal.pageSize.width / 2;
    const tituloY = marginTop + imgHeight + 10;
    const img = new Image();

    img.src = logoUrl;
    img.onload = () => {
      // Desenhar a logo
      doc.addImage(img, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);

      // Estilizar e adicionar o título
      doc.setFontSize(16);
      doc.setTextColor('#023341');
      doc.text(titulo, tituloX, tituloY, { align: 'center' });

      // Gerar tabela com estilos
      doc.autoTable({
        head: [colunasNomes],
        body: linhas,
        startY: tituloY + 10,
        headStyles: {
          fillColor: '#407590',
          textColor: '#ffffff',
          fontStyle: 'bold',
          halign: 'center',
        },
        bodyStyles: {
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: '#f3f3f3',
        },
        styles: {
          font: 'helvetica',
          fontSize: 10,
          cellPadding: 5,
        },
      });

      doc.save(`${nomeTabela}.pdf`);
    }
  }

  private exportarWord(data: any[], colunas: Coluna[], nomeTabela: string) {
    fetch('../../../../assets/app-assets/images/logo/logo-b-geia.png')
      .then(res => res.arrayBuffer())
      .then(logoBuffer => {
        const rows = data.map((dado) =>
          colunas.map((coluna) => dado[coluna.campo].toString())
        );
        const headerCells = colunas.map(
          (coluna) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: coluna.nome, bold: true, color: 'FFFFFF' })],
                  alignment: AlignmentType.CENTER,
                }),
              ],
              shading: { fill: "407590" },
              margins: { top: 100, bottom: 100, left: 100, right: 100 },
            }));

        const dataRows = rows.map((row, index) => new TableRow({
          children: row.map((text) => new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text })],
              alignment: AlignmentType.CENTER,
            })],
            shading: { fill: index % 2 === 0 ? "f3f3f3" : "FFFFFF" },
          })),
        }));

        const table = new Table({
          rows: [
            new TableRow({ children: headerCells }),
            ...dataRows,
          ],
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1 },
            bottom: { style: BorderStyle.SINGLE, size: 1 },
            left: { style: BorderStyle.SINGLE, size: 1 },
            right: { style: BorderStyle.SINGLE, size: 1 },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
            insideVertical: { style: BorderStyle.SINGLE, size: 1 },
          },
        });

        const title = new Paragraph({
          children: [
            new TextRun({
              text: nomeTabela,
              bold: true,
              color: "023341",
              size: 28,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        });

        const doc = new Document({
          sections: [
            {
              properties: {},
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: logoBuffer,
                      transformation: { width: 100, height: 50 },
                      type: "png", // Tipo da imagem explicitamente definido
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                }),
                title,
                table,
              ],
            },
          ],
        });

        Packer.toBlob(doc).then(blob => {
          saveAs(blob, `${nomeTabela}.docx`);
        });
      });



  }

  private exportarTXT(data: any[], colunas: Coluna[], nomeTabela: string) {
    const larguraColuna = colunas.map((coluna) => {
      const maxDataLength = Math.max(
        ...data.map((dado) => (dado[coluna.campo]?.toString() || '').length),
        coluna.nome.length
      );
      return maxDataLength + 2;
    });

    const ajustarTexto = (texto: string, largura: number) => {
      return texto.padEnd(largura, ' ').substring(0, largura);
    };

    const totalLargura = larguraColuna.reduce((acc, curr) => acc + curr, 0);
    const tituloCentralizado = nomeTabela
      .padStart((totalLargura + nomeTabela.length) / 2, ' ')
      .padEnd(totalLargura, ' ');

    const cabecalho = colunas
      .map((coluna, index) => ajustarTexto(coluna.nome, larguraColuna[index]))
      .join(' ');

    const linhas = data.map((dado) =>
      colunas
        .map((coluna, index) => ajustarTexto(dado[coluna.campo]?.toString() || '', larguraColuna[index]))
        .join(' ')
    );

    const conteudo = [tituloCentralizado, cabecalho, ...linhas].join('\n');

    const blob = new Blob([conteudo], { type: 'text/plain' });
    saveAs(blob, `${nomeTabela}.txt`);
  }

  private exportarExcel(data: any[], colunas: Coluna[], nomeTabela: string) {
    const dadosFormatados = data.map((dado) =>
      colunas.reduce<{ [key: string]: any }>((acc, coluna) => {
        acc[coluna.nome] = dado[coluna.campo];
        return acc;
      }, {})
    );
    const worksheet = XLSX.utils.json_to_sheet([]);

    XLSX.utils.sheet_add_aoa(worksheet, [[nomeTabela]], { origin: 'A1' });
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colunas.length - 1 } },
    ];

    XLSX.utils.sheet_add_json(worksheet, dadosFormatados, {
      origin: 'A2',
      skipHeader: false,
    });

    worksheet['!cols'] = colunas.map((coluna) => {
      const maxLength = Math.max(
        coluna.nome.length,
        ...data.map((dado) => (dado[coluna.campo]?.toString().length || 0)) // Comprimento do maior valor na coluna
      );
      return { wch: maxLength + 2 };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, nomeTabela);
    XLSX.writeFile(workbook, `${nomeTabela}.xlsx`);
  }

  //serviço de exportar arquivos blob recebidos do back
  // Método auxiliar para determinar o MIME type baseado no tipo de exportação
  buscarTipoMimeArquivo(tipo: number): string {
    switch (tipo) {
      case 0: // TXT
        return 'text/plain';
      case 1: // XLSX
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 2: // PDF
        return 'application/pdf';
      case 3: // DOCX
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 4: // CSV
        return 'text/csv';
      default:
        return 'application/octet-stream';
    }
  }

  // Método auxiliar para gerar nome do arquivo
  gerarNomeArquivo(tipo: number, origem: number): string {
    const telaOrigem = origem
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    switch (tipo) {
      case 0: // TXT
        if (Tela.lead == telaOrigem) {
          return `leads_${timestamp}.txt`;
        }
        return `cotação_${timestamp}.txt`;
      case 1: // XLSX
        if (Tela.lead == telaOrigem) {
          return `leads_${timestamp}.xlsx`;
        }
        return `cotação_${timestamp}.xlsx`;
      case 2: // PDF
        if (Tela.lead == telaOrigem) {
          return `leads_${timestamp}.pdf`;
        }
        return `cotação_${timestamp}.pdf`;
      case 3: // DOCX
        if (Tela.lead == telaOrigem) {
          return `leads_${timestamp}.docx`;
        }
        return `cotação_${timestamp}.docx`;
      case 4: // CSV
        if (Tela.lead == telaOrigem) {
          return `leads_${timestamp}.csv`;
        }
        return `cotação_${timestamp}.csv`;
      default:
        return `leads_${timestamp}.txt`;
    }
  }

}
